import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import { useState, FC } from 'react';
import { useMeasure } from 'react-use';

import { AppSlider } from './AppSlider';
import { isNormalValue } from './PrimarySlider';

import { Colors } from '@/assets';

interface IProps {
    min?: number;
    max?: number;
    step?: number;
    type?: 'dot' | 'square';
    unit?: 'cm' | '%';
}
export const StepSlider: FC<IProps> = ({ min = 0, max = 100, step = 1, type = 'dot', unit = 'cm' }) => {
    const [value, setValue] = useState<number>(0);
    const [ref, { width }] = useMeasure();
    const [isShowBox, setIsShowBox] = useState<boolean>(true);

    const length = max - min;
    const numberDot = Math.floor(max / step);

    const minPointBox = -10;
    const maxPointBox = width - 75;
    const widthStepBox = (maxPointBox - minPointBox) / length;
    const boxPosition = widthStepBox * value + minPointBox;

    const minPointDot = width * 0.1;
    const maxPointDot = width * 0.9;
    const widthStepDot = (maxPointDot - minPointDot) / length;
    const dotActivePosition = widthStepDot * value + minPointDot;

    return (
        <AppSlider>
            <Box ref={ref} sx={{ position: 'relative', width: 300 }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        width: '100%',
                        height: 2,
                        background: Colors.geyserGrey,
                        ':before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: 0,
                            width: dotActivePosition,
                            height: 2,
                            background: Colors.orangeRed
                        }
                    }}
                ></Box>
                {Array.from({ length: numberDot }).map((_, index) => {
                    const dotPosition = ((maxPointDot - minPointDot) / (numberDot - 1)) * index + minPointDot;
                    const isActiveDot = dotPosition < dotActivePosition;
                    return (
                        <Box
                            key={index}
                            sx={{
                                position: 'absolute',
                                width: 8,
                                height: 8,
                                backgroundColor: isActiveDot ? Colors.orangeRed : Colors.geyserGrey,
                                top: 5,
                                borderRadius: type === 'dot' ? '90px' : 0,
                                left: dotPosition
                            }}
                        ></Box>
                    );
                })}
                {!isShowBox && (
                    <Box
                        sx={{
                            position: 'absolute',
                            width: 12,
                            height: 12,
                            top: 3,
                            background: Colors.white,
                            border: '2px solid',
                            borderColor: Colors.orangeRed,
                            borderRadius: type === 'dot' ? '90px' : 0,
                            left: dotActivePosition
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: -24, left: '50%', transform: 'translateX(-50%)' }}>
                                <Typography sx={{ color: Colors.orangeRed }} variant="subtitle2">
                                    {min + value}
                                    {unit}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
                {isShowBox && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: '2px',
                            left: boxPosition
                        }}
                    >
                        <Box
                            sx={{
                                background: Colors.white,
                                border: '2px solid',
                                borderColor: Colors.geyserGrey,
                                boxSizing: 'border-box',
                                borderRadius: '30px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 75,
                                py: 1,
                                '&:hover': {
                                    borderColor: Colors.vividTangerineOrange
                                }
                            }}
                        >
                            <Typography sx={{ color: Colors.orangeRed }} variant="subtitle2">
                                {min + value}
                                {unit}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Box
                    component="input"
                    onMouseDown={() => {
                        if (isNormalValue(value, length)) {
                            setIsShowBox(false);
                        }
                    }}
                    onMouseUp={() => {
                        setIsShowBox(true);
                    }}
                    sx={{
                        '&::-webkit-slider-thumb': {
                            left: boxPosition + 20
                        }
                    }}
                    min={0}
                    max={length}
                    step={step}
                    value={value}
                    onChange={(e: any) => {
                        const value = Number(e.target.value);
                        setValue(value);
                        if (isNormalValue(value, length)) {
                            setIsShowBox(false);
                        } else {
                            setIsShowBox(true);
                        }
                    }}
                    type="range"
                    className="range"
                />
            </Box>
        </AppSlider>
    );
};
